import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiErrorMessage } from "@/api/client";

interface ShopeeConnection {
  id: number;
  country_code: string;
  shop_id: string;
  shop_name: string | null;
  status: string;
  connected_at: string | null;
  last_synced_at: string | null;
}

// 設計 §3 §4 で対応する Shopee の国
const SUPPORTED_COUNTRIES: { code: string; label: string }[] = [
  { code: "SG", label: "シンガポール (SG)" },
  { code: "MY", label: "マレーシア (MY)" },
  { code: "TH", label: "タイ (TH)" },
  { code: "TW", label: "台湾 (TW)" },
  { code: "ID", label: "インドネシア (ID)" },
  { code: "VN", label: "ベトナム (VN)" },
  { code: "PH", label: "フィリピン (PH)" },
  { code: "BR", label: "ブラジル (BR)" },
  { code: "MX", label: "メキシコ (MX)" },
];

interface CallbackFlash {
  kind: "success" | "error";
  message: string;
}

/**
 * 顧客側: Shopee 店舗接続管理画面。
 *
 * - 「Shopee と連携」ボタン → POST /oauth/start → Shopee 認可画面へ遷移
 * - 認可後の戻り URL は ?status=success/error&... なので画面上で通知表示
 * - 切断は DELETE /connections/<id>
 */
export function ShopeeConnectionsPage() {
  const [params, setParams] = useSearchParams();
  const [connections, setConnections] = useState<ShopeeConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedCountry, setSelectedCountry] = useState<string>(
    SUPPORTED_COUNTRIES[0].code,
  );
  const [startingAuth, setStartingAuth] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState<number | null>(null);

  // OAuth コールバック完了後のフラッシュメッセージ
  const flash = useMemo<CallbackFlash | null>(() => {
    const status = params.get("status");
    if (!status) return null;
    if (status === "success") {
      const shop = params.get("shop_id") || "";
      const cc = params.get("country_code") || "";
      return {
        kind: "success",
        message: `Shopee 店舗 ${cc ? `(${cc}) ` : ""}${shop} を連携しました。`,
      };
    }
    if (status === "error") {
      const code = params.get("code") || "";
      const message = params.get("message") || "認可に失敗しました";
      return { kind: "error", message: `${message}${code ? ` [${code}]` : ""}` };
    }
    return null;
  }, [params]);

  const clearFlash = useCallback(() => {
    // クエリパラメータを消して URL を綺麗にする
    const next = new URLSearchParams(params);
    ["status", "code", "message", "shop_id", "connection_id", "country_code"].forEach(
      (k) => next.delete(k),
    );
    setParams(next, { replace: true });
  }, [params, setParams]);

  const loadConnections = useCallback(() => {
    setLoading(true);
    setError(null);
    return api
      .get<{ connections: ShopeeConnection[] }>("/account/shopee/connections")
      .then((res) => setConnections(res.data.connections))
      .catch((e) => setError(apiErrorMessage(e, "接続一覧の取得に失敗しました")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const handleStartAuth = async () => {
    setStartingAuth(true);
    setError(null);
    try {
      const res = await api.post<{ authorize_url: string }>(
        "/account/shopee/oauth/start",
        { country_code: selectedCountry },
      );
      // Shopee 認可画面へ遷移 (戻りは GET /oauth/callback → SPA リダイレクト)
      window.location.href = res.data.authorize_url;
    } catch (e) {
      setError(apiErrorMessage(e, "認可開始に失敗しました"));
      setStartingAuth(false);
    }
  };

  const handleDisconnect = async (conn: ShopeeConnection) => {
    const label = conn.shop_name || conn.shop_id;
    if (!window.confirm(`店舗「${label}」(${conn.country_code}) を切断しますか？`)) {
      return;
    }
    setDisconnectingId(conn.id);
    setError(null);
    try {
      await api.delete(`/account/shopee/connections/${conn.id}`);
      await loadConnections();
    } catch (e) {
      setError(apiErrorMessage(e, "切断に失敗しました"));
    } finally {
      setDisconnectingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Shopee 店舗連携</h1>
      <p className="text-sm text-gray-600">
        Shopee セラーアカウントを連携すると、Shopee で発生した注文を毎朝自動取込し、
        確認画面で 1 クリック発注確定できます。SG / TW / MY 等の複数国・複数店舗の同時接続に対応しています。
      </p>

      {flash && (
        <div
          className={
            "rounded-md border px-4 py-3 text-sm flex justify-between items-start " +
            (flash.kind === "success"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800")
          }
        >
          <span>{flash.message}</span>
          <button
            onClick={clearFlash}
            className="ml-3 text-xs underline opacity-70 hover:opacity-100"
          >
            閉じる
          </button>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="card p-4">
        <h2 className="font-semibold mb-3">接続済店舗</h2>
        {loading ? (
          <p className="text-sm text-gray-500">読み込み中…</p>
        ) : connections.length === 0 ? (
          <p className="text-sm text-gray-500">
            まだ Shopee 店舗を接続していません。下の「店舗を追加」から始めてください。
          </p>
        ) : (
          <ul className="space-y-2">
            {connections.map((c) => (
              <li
                key={c.id}
                className="border rounded p-3 flex justify-between items-center text-sm"
              >
                <div>
                  <div className="font-semibold">
                    {c.shop_name || c.shop_id} ({c.country_code})
                  </div>
                  <div className="text-xs text-gray-500">
                    状態: {c.status} / 接続日:{" "}
                    {c.connected_at
                      ? new Date(c.connected_at).toLocaleString("ja-JP")
                      : "—"}
                    {c.last_synced_at && (
                      <>
                        {" "}
                        / 最終同期:{" "}
                        {new Date(c.last_synced_at).toLocaleString("ja-JP")}
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDisconnect(c)}
                  disabled={disconnectingId === c.id}
                  className="btn-secondary text-xs"
                >
                  {disconnectingId === c.id ? "切断中…" : "切断"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card p-4 bg-blue-50">
        <h2 className="font-semibold mb-3">店舗を追加</h2>
        <div className="space-y-3 text-sm">
          <p className="text-gray-700">
            連携したい Shopee 店舗の国を選んで「Shopee と連携」を押してください。
            Shopee の認可画面に遷移し、ログイン後に店舗を選択すると自動で接続が完了します。
          </p>
          <div className="flex gap-3 items-end">
            <label className="flex flex-col">
              <span className="label">国</span>
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="input"
                disabled={startingAuth}
              >
                {SUPPORTED_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              onClick={handleStartAuth}
              disabled={startingAuth}
              className="btn-primary"
            >
              {startingAuth ? "認可画面へ遷移中…" : "Shopee と連携"}
            </button>
          </div>
          <p className="text-xs text-gray-600">
            事前準備: Shopee 出品の Item SKU 欄に <strong>JAN</strong>{" "}
            を入力してください。SKU 未入力商品は取込対象外になります（詳細は申込時マニュアル参照）。
          </p>
        </div>
      </div>
    </div>
  );
}
